import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { MODAL_ANIMATION } from './tokens';
import { useModalAnimation } from '../../hooks/useModalAnimation';
import { useModalCloseProtection } from '../../hooks/useModalCloseProtection';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { lockScroll, unlockScroll } from '../../utils/scrollLock';

interface DialogShellProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly title?: string;
  readonly children: React.ReactNode;
  readonly size?: 'sm' | 'md' | '3xl' | 'lg' | 'xl';
  readonly icon?: React.ReactNode;
  readonly showCloseButton?: boolean;
  /** Bloquea cierre por Escape y backdrop. Si se omite, se deriva de `loading`. */
  readonly onBeforeClose?: () => Promise<boolean> | boolean;
  /** Deshabilita la X del header por defecto y bloquea Escape/backdrop. */
  readonly loading?: boolean;
  /**
   * Reemplaza el header completo (título + X) por JSX propio.
   * El componente sigue gestionando Escape, backdrop y foco.
   * Para que la X y el botón Cancelar de un header/footer propio pasen por la
   * confirmación de cambios sin guardar, cablearlos a `closeRef.current?.()`
   * (no a `onClose` directo, que omite el guard).
   */
  readonly header?: React.ReactNode;
  /** Nivel de apilado: 'base' = z-50 (default); 'nested' = z-[60], para una ventana que aparece sobre otra ya abierta. */
  readonly level?: 'base' | 'nested';
  /** Si true, al intentar cerrar con cambios pendientes muestra la confirmación "¿cerrar sin guardar?". */
  readonly hasUnsavedChanges?: boolean;
  /**
   * Recibe el cierre ya protegido por el guard de cambios sin guardar. Los botones X/Cancelar
   * de un header/footer propio deben llamar `closeRef.current?.()` para que la confirmación
   * "¿cerrar sin guardar?" salte en todos los caminos de cierre (X, Cancelar, Escape, clic-fuera).
   */
  readonly closeRef?: React.MutableRefObject<(() => void) | null>;
}

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export const DialogShell: React.FC<DialogShellProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  icon,
  showCloseButton = true,
  onBeforeClose,
  loading,
  header,
  level = 'base',
  hasUnsavedChanges = false,
  closeRef
}) => {
  const effectiveOnBeforeClose = onBeforeClose ?? (loading === undefined ? undefined : () => !loading);
  const { isMounted, isClosing, dataState } = useModalAnimation(isOpen);
  const { modalRef, handleClose, showCloseConfirmation, forceClose, cancelClose } = useModalCloseProtection({
    isOpen,
    onClose,
    hasUnsavedChanges,
    onBeforeClose: effectiveOnBeforeClose
  });

  // Expone el cierre protegido para que la X/Cancelar de un header/footer propio
  // pasen por la confirmación de cambios sin guardar (no solo Escape/backdrop).
  if (closeRef) closeRef.current = handleClose;

  // Focus trap — Tab cycling between first and last focusable element
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !modalRef.current) return;
      const focusable = Array.from(modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    const firstFocusable = modalRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    firstFocusable?.focus();

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, modalRef]);

  // Bloqueo de scroll mientras el modal está montado (apertura + animación de cierre),
  // con contador para apilados y compensación de scrollbar (evita el salto horizontal).
  useEffect(() => {
    if (!isMounted) return;
    lockScroll();
    return () => unlockScroll();
  }, [isMounted]);

  if (!isMounted) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    '3xl': 'max-w-[710px]',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl'
  };

  const zClass = level === 'nested' ? 'z-[60]' : 'z-50';

  // Portal a document.body: saca el overlay del flujo de la página para que
  // utilidades del contenedor padre (ej. `space-y-*`, que inyecta margin-top a
  // los hijos no-primeros) no desplacen el overlay `fixed`. Sin esto, el backdrop
  // queda corrido y no cubre la franja superior del viewport.
  return createPortal(
    <>
    <div
      data-modal-overlay="true"
      className={`fixed inset-0 ${zClass} flex items-center justify-center p-4 transition-opacity duration-200 ease-out animate-in fade-in-0 ${isClosing ? 'opacity-0' : 'opacity-100'}`}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <dialog
        ref={modalRef as unknown as React.RefObject<HTMLDialogElement>}
        open
        data-state={dataState}
        className={`relative bg-white rounded-2xl shadow-2xl w-full ${sizeClasses[size]} max-h-[90vh] overflow-y-auto overflow-x-hidden m-0 p-0 border-0 ${MODAL_ANIMATION}`}
        aria-modal="true"
      >
        {header ?? ((title || showCloseButton) && (
          <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between z-10">
            {title && (
              <div className="flex items-center gap-3">
                {icon && <div className="flex-shrink-0">{icon}</div>}
                <h2 className="text-2xl font-bold text-slate-800">{title}</h2>
              </div>
            )}
            {showCloseButton && (
              <button
                onClick={handleClose}
                disabled={loading}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Cerrar modal"
              >
                <X className="w-6 h-6 text-slate-600" />
              </button>
            )}
          </div>
        ))}
        <div className="p-6">
          {children}
        </div>
      </dialog>
    </div>
    <UnsavedChangesDialog
      show={showCloseConfirmation}
      onCancel={cancelClose}
      onConfirm={forceClose}
      zIndexClass={level === 'nested' ? 'z-[70]' : 'z-[60]'}
    />
    </>,
    document.body
  );
};
